/**
 * Product Quiz JavaScript - Scoring System
 * Handles points accumulation and result calculation based on tea types.
 */

class ProductQuiz {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.config = window.quizConfig?.[sectionId];
    this.currentQuestionIndex = 0;
    this.scores = {
      IST: 0, EGE: 0, SEK: 0, MAS: 0, FOR: 0, CAN: 0, CIL: 0, SIN: 0
    };

    // Tie-breaker priority
    this.tieBreaker = ['SEK', 'IST', 'CAN', 'EGE', 'CIL', 'MAS', 'FOR', 'SIN'];

    this.init();
  }

  init() {
    if (!this.config) {
      console.error('Quiz config not found for section:', this.sectionId);
      return;
    }

    this.questions = this.config.questions || [];
    this.totalQuestions = this.questions.length || document.querySelectorAll(`#questions-container-${this.sectionId} .quiz-question`).length || 12; // Fallback to 12 if everything fails

    if (this.totalQuestions === 0) {
      console.error('No questions found in quiz config');
      return;
    }

    // Cache DOM elements
    this.cacheElements();

    // Check for shared result
    const urlParams = new URLSearchParams(window.location.search);
    const sharedResult = urlParams.get('result');

    if (sharedResult && this.config.results.some(r => r.resultCode === sharedResult)) {
      this.showResult(sharedResult);
    } else {
      this.showQuestion(0);
    }

    // Initialize
    this.initProgress();
    this.attachEventListeners();
  }

  cacheElements() {
    this.progressFill = document.getElementById(`progress-${this.sectionId}`);
    this.currentQuestionEl = document.getElementById(`current-question-${this.sectionId}`);
    this.totalQuestionsEl = document.getElementById(`total-questions-${this.sectionId}`);
    this.questionsContainer = document.getElementById(`questions-container-${this.sectionId}`);
    this.prevBtn = document.getElementById(`prev-btn-${this.sectionId}`);
    this.retakeBtn = document.getElementById(`retake-btn-${this.sectionId}`);
    this.quizWrapper = document.getElementById(`quiz-${this.sectionId}`);
    this.resultsContainer = document.getElementById(`results-${this.sectionId}`);
    this.productsContainer = document.getElementById(`results-main-product-${this.sectionId}`); // Main product container
    this.upsellsContainer = document.getElementById(`results-upsells-${this.sectionId}`); // New upsells container
  }

  initProgress() {
    if (this.totalQuestionsEl) {
      this.totalQuestionsEl.textContent = this.totalQuestions;
    }
  }

  setProgress(questionNumber) {
    if (!this.progressFill) {
      this.progressFill = document.getElementById(`progress-${this.sectionId}`);
    }

    if (this.progressFill && this.totalQuestions > 0) {
      const progress = (questionNumber / this.totalQuestions) * 100;
      this.progressFill.style.width = progress + '%';
      this.progressFill.style.backgroundColor = '#790D5B';
      this.progressFill.style.display = 'block';
      this.progressFill.style.height = '100%';
    }

    if (this.currentQuestionEl) {
      this.currentQuestionEl.textContent = questionNumber;
    }
  }

  attachEventListeners() {
    const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (!section) return;

    section.addEventListener('click', (e) => {
      const option = e.target.closest('.quiz-option');
      if (option) {
        e.preventDefault();
        this.handleOptionClick(option);
      }
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToPreviousQuestion());
    }

    if (this.retakeBtn) {
      this.retakeBtn.addEventListener('click', () => this.retakeQuiz());
    }
  }

  handleOptionClick(option) {
    const questionEl = option.closest('.quiz-question');
    if (!questionEl) return;

    questionEl.querySelectorAll('.quiz-option').forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');

    const pointsString = option.dataset.points;
    this.saveAnswer(this.currentQuestionIndex, pointsString);

    setTimeout(() => {
      this.goToNextQuestion();
    }, 350);
  }

  saveAnswer(questionIndex, pointsString) {
    if (!this.answers) this.answers = {};
    this.answers[questionIndex] = pointsString;
  }

  calculateScores() {
    const currentScores = {
      IST: 0, EGE: 0, SEK: 0, MAS: 0, FOR: 0, CAN: 0, CIL: 0, SIN: 0
    };

    Object.values(this.answers || {}).forEach(pointsStr => {
      if (!pointsStr) return;

      const parts = pointsStr.split(',');
      parts.forEach(part => {
        const [code, value] = part.trim().split(':');
        if (code && value && currentScores.hasOwnProperty(code.trim())) {
          currentScores[code.trim()] += parseInt(value);
        }
      });
    });

    return currentScores;
  }

  getWinner(scores) {
    let winner = null;
    let maxScore = -1;

    Object.entries(scores).forEach(([code, score]) => {
      if (score > maxScore) {
        maxScore = score;
        winner = code;
      } else if (score === maxScore) {
        const currentWinnerIndex = this.tieBreaker.indexOf(winner);
        const contenderIndex = this.tieBreaker.indexOf(code);

        if (contenderIndex !== -1 && (currentWinnerIndex === -1 || contenderIndex < currentWinnerIndex)) {
          winner = code;
        }
      }
    });

    return winner;
  }

  goToNextQuestion() {
    const nextIndex = this.currentQuestionIndex + 1;

    if (nextIndex < this.totalQuestions) {
      this.showQuestion(nextIndex);
    } else {
      this.finishQuiz();
    }
  }

  goToPreviousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.showQuestion(this.currentQuestionIndex - 1);
    }
  }

  showQuestion(index) {
    if (!this.questionsContainer) return;

    const allQuestions = this.questionsContainer.querySelectorAll('.quiz-question');

    allQuestions.forEach(q => q.classList.remove('active'));

    const currentQuestion = allQuestions[index];
    if (currentQuestion) {
      currentQuestion.classList.add('active');
      this.currentQuestionIndex = index;
    }

    // Update progress
    this.setProgress(index);
    this.updateNavigation();
  }

  updateNavigation() {
    if (this.prevBtn) {
      this.prevBtn.style.display = this.currentQuestionIndex > 0 ? 'inline-flex' : 'none';
    }
  }

  finishQuiz() {
    const scores = this.calculateScores();
    const winnerCode = this.getWinner(scores);

    // Set progress to 100% when finishing
    this.setProgress(this.totalQuestions);

    console.log('Quiz finished. Scores:', scores);
    console.log('Winner:', winnerCode);

    this.showResult(winnerCode);
  }

  showResult(winnerCode) {
    if (this.quizWrapper) this.quizWrapper.style.display = 'none';
    if (this.resultsContainer) this.resultsContainer.style.display = 'block';

    const resultConfig = this.config.results.find(r => r.resultCode === winnerCode);

    if (resultConfig) {
      this.displayResultContent(resultConfig);
    } else {
      console.error('No result found for code:', winnerCode);
      if (this.config.results.length > 0) {
        this.displayResultContent(this.config.results[0]);
      }
    }
  }

  updateShareLinks(result) {
    if (!this.resultsContainer) return;

    // Use current URL but ensure we have the result param
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('result', result.resultCode);
    const shareUrl = encodeURIComponent(currentUrl.toString());

    // Clean Turkish share text
    // "My Lazika Tea Quiz Result: [Title]. What's yours?"
    const shareText = encodeURIComponent(`Benim Hisli Harmanım: ${this.stripHtml(result.resultTitle)}. Senin hisli harmanın hangisi?`);

    const twitterLink = this.resultsContainer.querySelector('.link--twitter');
    const facebookLink = this.resultsContainer.querySelector('.link--facebook');

    // Update Twitter (supports text + url)
    if (twitterLink) {
      twitterLink.href = `https://twitter.com/share?text=${shareText}&url=${shareUrl}`;
    }

    // Update Facebook (supports url only)
    if (facebookLink) {
      facebookLink.href = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
    }

    // Instagram stays static pointing to profile as requested
  }

  async displayResultContent(result) {
    if (!this.resultsContainer) return;

    const titleEl = this.resultsContainer.querySelector('.results-title');
    const descEl = this.resultsContainer.querySelector('.results-subtitle');

    if (titleEl && result.resultTitle) titleEl.textContent = this.decodeHtmlEntities(result.resultTitle);
    if (descEl && result.resultDescription) descEl.textContent = this.decodeHtmlEntities(result.resultDescription);

    // Update share links with result-specific URL/Text
    this.updateShareLinks(result);

    if (this.productsContainer) {
      this.productsContainer.innerHTML = `
        <div class="quiz-loading">
          <div class="loading__spinner">
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </div>
      `;

      const products = await this.fetchProducts(result);

      if (products.length > 0) {
        const mainProduct = products[0];
        const upsellProducts = products.slice(1);

        // Render Main Product
        this.productsContainer.innerHTML = `
          <div class="results-main-wrapper">
            ${this.renderMainProductCard(mainProduct)}
          </div>
        `;

        const variantInput = this.resultsContainer.querySelector(`#quiz-add-to-cart-variant-${this.sectionId}`);
        const addToCartBtn = this.resultsContainer.querySelector(`#quiz-add-to-cart-btn-${this.sectionId}`);
        if (variantInput && addToCartBtn) {
          const firstAvailableVariant = mainProduct.variants?.find(v => v.available) || mainProduct.variants?.[0];
          if (firstAvailableVariant && firstAvailableVariant.available) {
            variantInput.value = firstAvailableVariant.id;
            addToCartBtn.disabled = false;
            addToCartBtn.querySelector('span').textContent = 'Sepete Ekle';
          } else {
            variantInput.value = firstAvailableVariant ? firstAvailableVariant.id : '';
            addToCartBtn.disabled = true;
            addToCartBtn.querySelector('span').textContent = 'Tükendi';
          }
        }

        // Render Upsells in Separate Container
        if (this.upsellsContainer) {
          if (upsellProducts.length > 0) {
            this.upsellsContainer.innerHTML = `
              <div class="results-upsells-wrapper">
                <h3 class="results-upsells-title">Deneyimini Tamamla</h3>
                <div class="results-products-grid">
                  ${upsellProducts.map(p => this.renderUpsellProductCard(p)).join('')}
                </div>
              </div>
            `;
          } else {
            this.upsellsContainer.innerHTML = '';
          }
        }
      } else {
        this.productsContainer.innerHTML = '<p class="no-products-message">Bu sonuç için ürün bulunamadı.</p>';
        if (this.upsellsContainer) this.upsellsContainer.innerHTML = '';
      }
    }

    this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async fetchProducts(result) {
    const handles = result.productHandles || [];
    const promises = handles
      .filter(h => h)
      .map(handle => fetch(`/products/${handle}.js`).then(r => r.ok ? r.json() : null));

    const products = (await Promise.all(promises)).filter(p => p);
    return products;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'TRY';
    const amount = cents / 100;

    if (currency === 'TRY') {
      return amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';
    }
    return amount.toLocaleString('en-US', { style: 'currency', currency: currency });
  }

  decodeHtmlEntities(text) {
    if (!text) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = text;
    return tmp.textContent || tmp.innerText || '';
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  truncateText(text, maxLength = 150) {
    if (!text) return '';
    const stripped = this.stripHtml(text);
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + '...';
  }

  getProductTags(product) {
    // Get up to 3 relevant tags for display
    const excludeTags = ['hide-product', 'featured', 'sale', 'new'];
    const tags = (product.tags || [])
      .filter(tag => !excludeTags.some(exc => tag.toLowerCase().includes(exc)))
      .slice(0, 3);
    return tags;
  }

  renderMainProductCard(product) {
    const image = product.featured_image || product.images?.[0] || '';
    const price = product.price;
    const comparePrice = product.compare_at_price;
    const title = product.title;
    const url = product.url;
    const hasDiscount = comparePrice && comparePrice > price;
    const description = this.truncateText(product.description, 180);
    let productType = product.type || '';

    const tags = this.getProductTags(product);
    const vendor = product.vendor || '';

    // Calculate discount percentage
    let discountPercent = 0;
    if (hasDiscount) {
      discountPercent = Math.round((1 - price / comparePrice) * 100);
    }

    return `
      <a href="${url}" class="results-product-card main-product">
        <div class="results-product-info">
          ${productType ? `<span class="results-product-type">${productType}</span>` : ''}
          <h3 class="results-product-title">${title}</h3>
          ${description ? `<p class="results-product-description">${description}</p>` : ''}
          
          ${tags.length > 0 ? `
            <div class="results-product-tags">
              ${tags.map(tag => `<span class="results-product-tag">${tag}</span>`).join('')}
            </div>
          ` : ''}
          
          <div class="results-product-price-wrapper">
            ${hasDiscount ? `<span class="results-product-compare-price">${this.formatMoney(comparePrice)}</span>` : ''}
            <span class="results-product-price">${this.formatMoney(price)}</span>
          </div>
        </div>
        <div class="results-product-image-wrapper">
          <img src="${image}" alt="${title}" class="results-product-image" loading="lazy">
          ${hasDiscount ? `<span class="results-product-badge">%${discountPercent} İndirim</span>` : ''}
        </div>
      </a>
    `;
  }

  renderUpsellProductCard(product) {
    const image = product.featured_image || product.images?.[0] || '';
    const price = product.price;
    const comparePrice = product.compare_at_price;
    const title = product.title;
    const url = product.url;
    const hasDiscount = comparePrice && comparePrice > price;
    const productType = product.type || '';

    let discountPercent = 0;
    if (hasDiscount) {
      discountPercent = Math.round((1 - price / comparePrice) * 100);
    }

    return `
      <a href="${url}" class="results-product-card upsell-product">
        <div class="results-product-image-wrapper">
          <img src="${image}" alt="${title}" class="results-product-image" loading="lazy">
          ${hasDiscount ? `<span class="results-product-badge">%${discountPercent}</span>` : ''}
        </div>
        <div class="results-product-info">
          ${productType ? `<span class="results-product-type">${productType}</span>` : ''}
          <h4 class="results-product-title">${title}</h4>
          <div class="results-product-price-wrapper">
            ${hasDiscount ? `<span class="results-product-compare-price">${this.formatMoney(comparePrice)}</span>` : ''}
            <span class="results-product-price">${this.formatMoney(price)}</span>
          </div>
        </div>
      </a>
    `;
  }

  retakeQuiz() {
    this.currentQuestionIndex = 0;
    this.answers = {};

    if (this.quizWrapper) this.quizWrapper.style.display = '';
    if (this.resultsContainer) this.resultsContainer.style.display = 'none';

    this.showQuestion(0);

    const allOptions = document.querySelectorAll(`#questions-container-${this.sectionId} .quiz-option`);
    allOptions.forEach(opt => opt.classList.remove('selected'));
  }
}

// Initialize when DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-quiz-section').forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (sectionId) {
      new ProductQuiz(sectionId);
    }
  });
});